import { defineStore } from 'pinia';
import { storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';

import { websocketConnection } from '@/assets/js/websocketapi.js';
import { useExpertStore } from '@/stores/expert.js';
import { useUserStore } from '@/stores/user.js';
import { mainAxios as axios } from '@/assets/js/axios_config';
import { defaultErrorHandling } from '@/assets/js/utils.js';

export const useProcessStore = defineStore('process', () => {
    const { dashboard_experts } = storeToRefs(useExpertStore());
    const { selected_workspace_id } = storeToRefs(useUserStore());

    const processLoading = ref(false);
    const processes = ref({});
    const historyWorkspaceProcesses = ref([]);
    const failedConnections = ref([]);
    const pollingIntervalId = ref(null);
    const websocketConnections = ref({});

    const currentWorkspaceProcesses = computed(() => {
        return Object.values(processes.value).filter((process) => {
            return (
                process.type !== 'unresolved_website' &&
                dashboard_experts.value.some((expert) => expert.expert_id === process.expert_id)
            );
        });
    });
    const currentUnresolvedProcesses = computed(() => {
        return Object.values(processes.value).filter((process) => {
            return (
                process.type === 'unresolved_website' &&
                dashboard_experts.value.some((expert) => expert.expert_id === process.expert_id)
            );
        });
    });

    const fetchWorkspaceProcesses = async (status_filter = 'inactive') => {
        await axios
            .get(
                `/process/get_workspace_processes/${selected_workspace_id.value}?status_filter=${status_filter}`,
            )
            .then((response) => {
                if (status_filter === 'inactive') {
                    historyWorkspaceProcesses.value = response.data;
                } else if (status_filter === 'active') {
                    processes.value = {};
                    response.data.forEach((process) => {
                        processes.value[process.id] = {
                            ...process,
                            expert_id: process.expert_model_id,
                        };
                        if (process.ws_token) {
                            connectToProcessSocket(process.id, process.ws_token.url);
                        }
                    });
                }
            })
            .catch(defaultErrorHandling());
    };

    const fetchUnresolvedProcesses = async () => {
        // get uncommitted webpages
        await axios
            .get(`/expert/crud/uncommitted/workspace/${selected_workspace_id.value}`)
            .then((response) => {
                response.data.forEach((website) => {
                    const processId = `${website.expert_id}-${website.website_id}`;
                    processes.value[processId] = {
                        expert_id: website.expert_id,
                        id: processId,
                        type: 'unresolved_website',
                        state: {
                            urls: [website.website_url],
                            website_ids: [website.website_id],
                            crawled_pages: website.webpages,
                        },
                    };
                });
            })
            .catch(defaultErrorHandling());
    };

    // Actions
    const addProcess = async (processId, connectionUrl, processData = null) => {
        try {
            let processToAdd;

            if (processData) {
                processToAdd = {
                    id: processId,
                    connection_url: connectionUrl,
                    expert_id: processData.expert_model_id,
                    type: processData.type,
                    status: processData.status,
                    state: processData.state,
                };
            } else {
                const response = await axios.get(`/process/${processId}`);
                const fetchedData = response.data;

                processToAdd = {
                    id: processId,
                    connection_url: connectionUrl,
                    expert_id: fetchedData.expert_model_id,
                    type: fetchedData.type,
                    status: fetchedData.status,
                    state: fetchedData.state,
                };
            }

            // Add the process to the store
            processes.value[processId] = processToAdd;

            // Connect to socket if URL provided
            if (connectionUrl) {
                connectToProcessSocket(processId, connectionUrl);
            }
        } catch (error) {
            defaultErrorHandling()(error);
        }
    };

    const removeProcess = (processId) => {
        delete processes.value[processId];
        if (websocketConnections.value[processId]) {
            websocketConnections.value[processId].close();
            delete websocketConnections.value[processId];
        }

        // refresh the process history
        fetchWorkspaceProcesses('active');
        fetchUnresolvedProcesses();
    };

    const stopProcess = async (processId) => {
        await axios
            .post(`/process/stop/${processId}`)
            .then((response) => {
                updateProcess(processId, response.data);
            })
            .catch(defaultErrorHandling());
    };

    const updateProcessesWithFailedConnection = async () => {
        const response = await axios.post(`/process/get_processes_by_id/`, {
            process_ids: failedConnections.value,
        });

        response.data.forEach((process) => {
            processes.value[process.id] = {
                ...process,
                expert_id: process.expert_model_id,
            };
        });
        failedConnections.value = failedConnections.value.filter((id) => {
            const process = processes.value[id];
            if (!process) return true;
            return !(
                process.status === 'failed' ||
                process.status === 'stopped' ||
                process.status === 'completed'
            );
        });

        if (failedConnections.value.length === 0) {
            clearInterval(pollingIntervalId.value);
            pollingIntervalId.value = null;
        }
    };

    const startPollingFailedConnections = () => {
        if (pollingIntervalId.value !== null) {
            return;
        }
        pollingIntervalId.value = setInterval(updateProcessesWithFailedConnection, 5000);
    };

    const connectToProcessSocket = async (processId, connectionUrl) => {
        if (websocketConnections.value[processId]) {
            return;
        }

        websocketConnections.value[processId] = await websocketConnection(connectionUrl, {
            onMessage: (message) => {
                const messageData = JSON.parse(message.data);
                handleWebsocketMessage(processId, messageData.data.process_state);
            },
            onError: (error) => {
                console.error('WebSocket error for process', processId, error);
                failedConnections.value.push(processId);
                if (!pollingIntervalId.value) {
                    startPollingFailedConnections();
                }
            },
        });
    };

    const handleWebsocketMessage = (processId, messageData) => {
        updateProcess(processId, messageData);
    };

    const updateProcess = (processId, messageData) => {
        if (!messageData) return;
        processes.value[processId] = {
            ...processes.value[processId],
            ...messageData,
        };

        if (
            messageData.status === 'completed' ||
            messageData.status === 'failed' ||
            messageData.status === 'stopped'
        ) {
            websocketConnections.value[processId].close();
            delete websocketConnections.value[processId];
        }
    };

    const setHistoryWorkspaceProcesses = (processes) => {
        historyWorkspaceProcesses.value = processes;
    };

    const appendHistoryWorkspaceProcesses = (newProcesses) => {
        // Filter out any duplicates by id
        const newUniqueProcesses = newProcesses.filter(
            (newProc) =>
                !historyWorkspaceProcesses.value.some(
                    (existingProc) => existingProc.id === newProc.id,
                ),
        );

        historyWorkspaceProcesses.value = [
            ...historyWorkspaceProcesses.value,
            ...newUniqueProcesses,
        ];
    };

    const submitSelectedPages = async ({ processId, expertId, websiteIds, selectedPageIds }) => {
        axios
            .post(`/expert/create/vectorize_website`, {
                expert_id: expertId,
                website_ids: websiteIds,
                webpage_ids: selectedPageIds,
            })
            .then((response) => {
                removeProcess(processId);
                addProcess(response.data['process_id'], response.data['ws_token'].url);
            })
            .catch(defaultErrorHandling());
    };

    watch(selected_workspace_id, async () => {
        if (!selected_workspace_id.value) return;
        processLoading.value = true;
        try {
            await Promise.all([fetchWorkspaceProcesses('active'), fetchUnresolvedProcesses()]);
        } catch (error) {
            defaultErrorHandling()(error);
        } finally {
            processLoading.value = false;
        }
    });

    const $reset = () => {
        processes.value = {};
        historyWorkspaceProcesses.value = [];
        failedConnections.value = [];

        if (pollingIntervalId.value !== null) {
            clearInterval(pollingIntervalId.value);
            pollingIntervalId.value = null;
        }
    };

    return {
        processes,
        currentWorkspaceProcesses,
        currentUnresolvedProcesses,
        historyWorkspaceProcesses,
        processLoading,

        addProcess,
        updateProcess,
        removeProcess,
        stopProcess,
        fetchWorkspaceProcesses,
        setHistoryWorkspaceProcesses,
        appendHistoryWorkspaceProcesses,
        fetchUnresolvedProcesses,
        submitSelectedPages,
        $reset,
    };
});
